import { Button, ButtonGroup, Grid, MenuItem, Paper } from "@material-ui/core";
import { Add, Remove } from "@material-ui/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { getBuildingSettingOption } from "../../../../api/buildingSettingApi";
import { getQueueFinanceDisplay, getQueueRoom } from "../../../../api/queueApi";
import { useForceRender } from "../../../../utils/customHook";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import SelectDropOtions from "../../../components/SelectDropOtions";
import { useStyleDisplay } from "../helpers/useStyleDisplay";
import { useStyleMedicineAndCashier } from "../helpers/useStyleMedicineAndCashier";
import { getTextSetting } from "../../../../api/textSettingApi";
import CashierDisplayDateTime from "./CashierDisplayDateTime";

const synth = window.speechSynthesis;

export default function CashierDisplay() {
    const [render, forceRender] = useForceRender();
    const [valueZoom, setValueZoom] = useState(1);
    const classsDisplay = useStyleDisplay();
    const classDisplayCashier = useStyleMedicineAndCashier(valueZoom);

    // ref
    const controllerRef = useRef(null);
    const containerRef = useRef(null);
    const titleRef = useRef(null);

    // state
    const [buildingId, setBuildingId] = useState("");
    const [floorId, setFloorId] = useState("");
    const [floorObj, setFloorObj] = useState({});
    const [showContent, setShowContent] = useState(false);
    const [option, setOption] = useState({ building: [], floor: [] });
    const [drugReceptionRoom, setDrugReceptionRoom] = useState([]);
    const [detailQueuePatient, setDetailQueuePatient] = useState({
        patientsInWait: [],
        patientsInHold: [],
        patientsInRoom: [],
    });
    const [movingText, setMovingText] = useState("");
    const [lastestQueue, setLastestQueue] = useState("");

    // text to speach
    const [speakTextQueue, setSpeakTextQueue] = useState([]);
    const [isInitialRoom, setIsInitialRoom] = useState(true);
    const [isFromUpdateHook, setIsFromUpdateHook] = useState(false);
    const [isFromPaymentManagement, setIsFromPaymentManagement] = useState(false);
    const voiceTH = synth.getVoices()?.find(item => item?.lang === "th-TH");
    const voiceEN = synth.getVoices()?.find(item => item?.lang === "en-US");

    // add fontsize
    const incrementFontSize = useCallback(
        type => {
            if (type === "up") {
                return setValueZoom(prev => prev + 0.25);
            }
            if (valueZoom <= 1) {
                if (valueZoom !== 0) {
                    return setValueZoom(prev => prev - 0.25);
                }
                return setValueZoom(0);
            }
            return setValueZoom(prev => prev - 0.25);
        },
        [valueZoom],
    );

    // fetch queue finance display
    const fetchQueueFinance = useCallback(async value => {
        try {
            const res = await getQueueFinanceDisplay({ locationCode: value?.locationCode });
            if (res) {
                setDetailQueuePatient(res);
                return res;
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // fetch examination room
    const fetchRoom = useCallback(async (building = null, floor = null) => {
        try {
            const createBody = {
                builingId: building ? building : 0,
                floorId: floor ? floor : 0,
                statusRoom: "2",
            };
            const res = await getQueueRoom(createBody);
            if (res) {
                setDrugReceptionRoom(res?.data);
                setIsInitialRoom(false);
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // function change input dropdown
    const handleInputChange = useCallback(
        async (type, val) => {
            if (type === 1) {
                setBuildingId(val);
                setFloorId("");
                setDrugReceptionRoom([]);
            } else if (type === 2) {
                setFloorId(val);
                setFloorObj(option.floor.find(item => item?.floorId === val));
                setIsInitialRoom(true);
                await fetchRoom(buildingId, val);
            }
        },
        [fetchRoom, buildingId, option.floor],
    );

    // fetch building setting option
    const fetchBuildingSettingOption = useCallback(async signal => {
        try {
            const buildingSettingOption = await getBuildingSettingOption(signal);
            if (buildingSettingOption) {
                setOption(currentState => ({
                    ...currentState,
                    building: buildingSettingOption?.buildingOption,
                    floor: buildingSettingOption?.floorOption,
                }));
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // fetch text setting
    const fetchTextSetting = useCallback(async signal => {
        try {
            const textSetting = await getTextSetting(signal);
            if (textSetting) {
                setMovingText(textSetting?.cashierText);
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // run only first render
    useEffect(() => {
        controllerRef.current = new AbortController();
        const init = async () => {
            await fetchBuildingSettingOption(controllerRef.current?.signal);
            await fetchTextSetting(controllerRef.current?.signal);
        };
        init();
        // to avoid memory leaked.
        return () => {
            controllerRef.current.abort();
        };
    }, [fetchBuildingSettingOption, fetchTextSetting]);

    // toggle show content
    useEffect(() => {
        if (buildingId !== "" && floorId !== "") {
            setShowContent(true);
        } else {
            setShowContent(false);
        }
    }, [buildingId, floorId]);

    // re-render
    useEffect(() => {
        const reRender = async () => {
            const financeQueueRes = await fetchQueueFinance({
                locationCode: floorObj?.locationCode,
            });
            if (financeQueueRes) {
                if (
                    detailQueuePatient.patientsInRoom.length <= financeQueueRes?.patientsInRoom?.length &&
                    !isInitialRoom
                ) {
                    const findLastestQueue = financeQueueRes?.patientsInRoom?.find(
                        patient => patient?.QueueNo === lastestQueue,
                    );
                    const findLastestQueueRoom = drugReceptionRoom.find(
                        item => item?.roomId === findLastestQueue?.CashierRoom,
                    );
                    if (
                        findLastestQueue?.QueueNo &&
                        findLastestQueueRoom?.name &&
                        !isFromUpdateHook &&
                        !isFromPaymentManagement
                    ) {
                        const queueNumber = findLastestQueue?.QueueNo?.split("")?.join(" ") || "";
                        const roomNameTH = findLastestQueueRoom?.name || "";
                        const roomNameEN = findLastestQueueRoom?.nameEn || "";
                        setSpeakTextQueue(currentState => [
                            ...currentState,
                            {
                                text:
                                    findLastestQueue?.Lang === "en"
                                        ? `Queue number ${queueNumber}, please contact ${roomNameEN}.`
                                        : `ขอเชิญหมายเลข  ${queueNumber}   ที่${roomNameTH}ครับ`,
                                lang: findLastestQueue?.Lang || "th",
                            },
                        ]);
                    }
                }
                setIsFromUpdateHook(false);
                setIsFromPaymentManagement(false);
            }
        };
        reRender();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [render, fetchQueueFinance, floorObj, drugReceptionRoom, setIsFromUpdateHook, setIsFromPaymentManagement]);

    // connect socket.io
    useEffect(() => {
        let socket;
        if (floorId) {
            socket = io(`${process.env?.REACT_APP_HOST_SOCKET}/cashier-queue`, {
                transports: ["websocket"],
            });

            socket.on("connect", () => {
                socket.emit("join-cashier-room", floorId);
            });

            socket.on("display-cashier-queue", data => {
                if (data === "update-hook") {
                    setIsFromUpdateHook(true);
                }
                if (
                    data?.type === "update-from-payment-management" &&
                    ["0", "2", "3", "4"].includes(data?.CashierStatus)
                ) {
                    setIsFromPaymentManagement(true);
                }
                setLastestQueue(data?.QueueNo);
                forceRender();
            });
        }

        return () => {
            if (floorId && socket) {
                socket.off("connect");
                socket.off("join-cashier-room");
                socket.off("display-cashier-queue");
                socket.off("disconnect");
                socket.emit("leave-cashier-room");
            }
        };
    }, [floorId, forceRender]);

    // auto speak
    useEffect(() => {
        if (speakTextQueue.length) {
            const utterance = new SpeechSynthesisUtterance(speakTextQueue[0]?.text);
            utterance.voice = speakTextQueue[0]?.lang === "en" ? voiceEN : voiceTH;
            utterance.rate = 0.5;
            utterance.pitch = 0.6;
            synth.speak(utterance);
        }
        setSpeakTextQueue(currentState => {
            currentState.shift();
            return currentState;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [speakTextQueue]);

    return (
        <Paper elevation={0} className={`px-3 ${classDisplayCashier().container}`} style={{ minHeight: "100vh" }}>
            <div className="h-100 d-flex flex-column">
                {/* Head title */}
                <div className="mb-4 mb-md-0">
                    <Grid
                        container
                        className="align-items-center py-5 shadow-sm rounded"
                        style={{ background: "linear-gradient(90deg, rgba(182, 227, 249, 0.55) 0%, #FFFFFF 100%)" }}>
                        <Grid item xs={12} lg={6} className="px-3 mb-3 mb-xl-0">
                            <CashierDisplayDateTime />
                        </Grid>
                        <Grid item xs={12} lg={6} className="px-3">
                            <Grid container spacing={1}>
                                <Grid item xs={12} lg={2}>
                                    <div className="d-flex flex-column">
                                        <ButtonGroup
                                            disableElevation
                                            className={`flex-fill ${classsDisplay.btnUpsizeFont}`}>
                                            <Button onClick={() => incrementFontSize("down")}>
                                                <Remove />
                                            </Button>
                                            <Button variant="text" className="flex-fill text-show-value text-center">
                                                x{valueZoom}
                                            </Button>
                                            <Button onClick={() => incrementFontSize("up")}>
                                                <Add />
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <SelectDropOtions
                                        options={option?.building}
                                        useTheme={true}
                                        onChange={value => handleInputChange(1, value)}
                                        value={buildingId}
                                        placeholder="Select..."
                                        containerClass={classsDisplay?.inputDropdownText}
                                        papperClass={classsDisplay.dropdownMenuText}
                                        renderOption={row => {
                                            return (
                                                <MenuItem key={row.buildingId + "_build"} value={row.buildingId}>
                                                    {" "}
                                                    {row.name}{" "}
                                                </MenuItem>
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <SelectDropOtions
                                        options={option?.floor.filter(item => item?.buildingId === buildingId)}
                                        useTheme={true}
                                        onChange={value => handleInputChange(2, value)}
                                        value={floorId}
                                        placeholder="Select..."
                                        containerClass={classsDisplay?.inputDropdownText}
                                        papperClass={classsDisplay.dropdownMenuText}
                                        renderOption={row => {
                                            return (
                                                <MenuItem key={row.floorId + "_floor"} value={row.floorId}>
                                                    {" "}
                                                    {row.name} {row.locationName}
                                                </MenuItem>
                                            );
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>

                {/* content */}
                <div className="flex-fill px-5">
                    {showContent && (
                        <div className="d-flex flex-column h-100">
                            <div className="flex-grow-0">
                                <div className="my-3 d-flex">
                                    <div>
                                        <div className="d-flex justify-content-center align-items-center">
                                            <img
                                                src={toAbsoluteUrl("/media/HCH/logos/logo-big.png")}
                                                className="img-fluid img-logo"
                                                alt="logo"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-fill box-field-call d-flex align-items-center">
                                        <Grid container spacing={2} className="h-100">
                                            {drugReceptionRoom?.map((row, idx) =>
                                                idx < 3 ? (
                                                    <Grid item xs={12} lg={4} key={"room_" + idx}>
                                                        <div
                                                            className={`d-flex align-items-stretch p-2 py-5 h-100 rounded shadow ${classsDisplay.bgBlue} ${classsDisplay.shadow}`}
                                                            style={
                                                                {
                                                                    // background: "linear-gradient(to right, #00b0e1, #9dddff)",
                                                                }
                                                            }>
                                                            <div
                                                                className="flex-fill d-flex align-items-center justify-content-center"
                                                                style={{ borderRight: "1px solid #fff" }}>
                                                                {/* <p className="mb-0 h3 text-white">{idx + 1}</p> */}
                                                                <p className="mb-0 text-center text-white text-call">
                                                                    {row?.name}
                                                                </p>
                                                            </div>
                                                            <div
                                                                className="flex-fill d-flex align-items-center justify-content-center"
                                                                style={{ borderLeft: "1px solid #fff" }}>
                                                                <p className="mb-0 text-center w-100 text-white text-call-number">
                                                                    {
                                                                        detailQueuePatient?.patientsInRoom.find(
                                                                            item => item?.CashierRoom === row?.roomId,
                                                                        )?.QueueNo
                                                                    }
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </Grid>
                                                ) : null,
                                            )}
                                        </Grid>
                                    </div>
                                </div>
                            </div>

                            {/* content */}
                            <div className="flex-fill mt-5">
                                <Grid innerRef={containerRef} container spacing={2} className="h-100">
                                    <Grid
                                        item
                                        xs={12}
                                        lg={6}
                                        className={`d-flex flex-column pt-0 ${classsDisplay?.borderR}`}>
                                        <div
                                            ref={titleRef}
                                            className={`py-5 bg rounded shadow mb-3 ${classsDisplay.bgGray} ${classsDisplay.shadow}`}>
                                            <p className="text-center text-xxl">คิวรอชำระเงิน</p>
                                        </div>
                                        <div className={`grid-container`}>
                                            {detailQueuePatient?.patientsInWait.map((row, idx) => {
                                                const hContainer = containerRef.current?.offsetHeight || 0;
                                                const hTitle = titleRef.current?.offsetHeight || 0;
                                                const colsHeight = (hContainer - hTitle * 1.1) / 4;
                                                if (idx >= 20) return null;
                                                return (
                                                    <div
                                                        className="grid-cols"
                                                        style={{ height: colsHeight }}
                                                        key={"queue_wait_" + idx}>
                                                        <div
                                                            className={`flex-fill shadow-sm border d-flex align-items-center rounded ${classsDisplay.bgGray}`}>
                                                            <p className="text-center w-100 text-xxl">{row?.QueueNo}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        lg={6}
                                        className={`d-flex flex-column pt-0 ${classsDisplay.borderL}`}>
                                        <div
                                            className={`py-5 rounded shadow mb-3 ${classsDisplay.textBlue} ${classsDisplay.shadow}`}>
                                            <p className="text-center text-xxl">คิวการเงินที่เรียกผ่านไปแล้ว</p>
                                        </div>
                                        <div className={`grid-container`}>
                                            {detailQueuePatient?.patientsInHold.map((row, idx) => {
                                                const hContainer = containerRef.current?.offsetHeight || 0;
                                                const hTitle = titleRef.current?.offsetHeight || 0;
                                                const colsHeight = (hContainer - hTitle * 1.1) / 4;
                                                if (idx >= 20) return null;
                                                return (
                                                    <div
                                                        className="grid-cols"
                                                        style={{ height: colsHeight }}
                                                        key={"queue_wait_" + idx}>
                                                        <div className="flex-fill bg-white shadow-sm border d-flex align-items-center rounded">
                                                            <p
                                                                className={`text-center text-xxl w-100 ${classsDisplay.textBlue}`}>
                                                                {row?.QueueNo}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </div>
                    )}
                </div>

                {/* footer */}
                <div className="mt-4 mt-md-3">
                    <Grid
                        container
                        className="align-items-center py-5 shadow-sm rounded"
                        // style={{ background: "linear-gradient(to right, #00b0e1, #9dddff)" }}
                        style={{ background: "rgb(77,167,218)" }}>
                        <Grid item xs={12} className="px-3">
                            <div className="d-flex align-items-center justify-content-center">
                                <div>
                                    <h3
                                        className={`text-white mb-0 text-center text-xl-left ${classsDisplay?.footerTextMarquee}`}>
                                        {movingText}
                                    </h3>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Paper>
    );
}
